import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShopLayout } from "@/components/ShopLayout";
import { ProductCard, ProductCardSkeleton, type ShopProduct } from "@/components/ProductCard";
import { api, ApiError } from "@/lib/api";
import { loadCart, saveCart, type CartLine } from "@/lib/cart";

export function CatalogPage() {
  const [articles, setArticles] = useState<ShopProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartLine[]>(() => loadCart());
  const [addingId, setAddingId] = useState<string | null>(null);

  const refreshCart = useCallback(() => {
    setCart(loadCart());
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ articles: ShopProduct[] }>("/api/v1/public/shop/catalog", { auth: false })
      .then((data) => {
        if (!cancelled) setArticles(data.articles);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Katalog konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function addToCart(article: ShopProduct) {
    setAddingId(article.id);
    setError(null);
    let pickupDate: string | null = null;
    if (article.requires_pickup_receipt) {
      try {
        const res = await api.get<{ pickup_date: string }>(
          `/api/v1/public/shop/articles/${article.id}/pickup-date`,
          { auth: false },
        );
        pickupDate = res.pickup_date;
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : "Abholdatum konnte nicht ermittelt werden.",
        );
        setAddingId(null);
        return;
      }
    }

    const existing = cart.find((line) => line.articleId === article.id);
    const next: CartLine[] = existing
      ? cart.map((line) =>
          line.articleId === article.id
            ? { ...line, quantity: line.quantity + 1, pickupDate: pickupDate ?? line.pickupDate }
            : line,
        )
      : [
          ...cart,
          {
            articleId: article.id,
            name: article.name,
            unit: article.unit,
            priceGross: Number(article.price_gross),
            quantity: 1,
            requiresPickupReceipt: article.requires_pickup_receipt,
            pickupDate,
          },
        ];
    saveCart(next);
    setCart(next);
    setAddingId(null);
  }

  const cartCount = cart.reduce((n, l) => n + l.quantity, 0);

  return (
    <ShopLayout>
      <div className="shop-page space-y-8">
        <header className="animate-fade-in">
          <h1 className="shop-page-title">Vorbestellen</h1>
          <p className="shop-page-subtitle">
            Frische Brötchen, Getränke und mehr – bequem vom Platz aus bestellen. Abholartikel werden
            zum nächstmöglichen Termin vorgemerkt.
          </p>
        </header>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i}>
                <ProductCardSkeleton />
              </li>
            ))}
          </ul>
        ) : articles.length === 0 ? (
          <div className="shop-card flex flex-col items-center gap-3 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                />
              </svg>
            </div>
            <p className="text-sm text-slate-600">Derzeit sind keine Artikel im Shop freigeschaltet.</p>
          </div>
        ) : (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <li key={article.id}>
                <ProductCard
                  article={article}
                  adding={addingId === article.id}
                  onAdd={() => void addToCart(article)}
                />
              </li>
            ))}
          </ul>
        )}

        {cartCount > 0 ? (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200/80 bg-white/90 p-4 backdrop-blur-xl sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
            <div className="shop-page !pb-0 !pt-0">
              <Link
                to="/warenkorb"
                onClick={refreshCart}
                className="shop-btn-primary w-full shadow-lg sm:mx-auto sm:max-w-md"
              >
                Zum Warenkorb · {cartCount} {cartCount === 1 ? "Artikel" : "Artikel"}
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </ShopLayout>
  );
}
